import { Box, Button, Card, InputAdornment, TextField, Typography, alpha, useTheme } from "@mui/material";
import Iconify from "components/Iconify";
import { t } from "i18next";

type CustomSearchBarProps = {
    value: string;
    onChange: (value: string) => void;
    onClear: () => void;
    placeholder?: string;
    filteredCount?: number;
};

const CustomSearchBar = ({
    value,
    onChange,
    onClear,
    placeholder = t("search"),
    filteredCount,
}: CustomSearchBarProps) => {
    const theme = useTheme();

    return (
        <>
            {/* Custom Search Bar */}
            <Card
                sx={{
                    mb: 2,
                    borderRadius: 3,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.grey[500], 0.08)}`,
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                }}
            >
                <Box sx={{ p: 2 }}>
                    <TextField
                        fullWidth
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Box
                                        sx={{
                                            p: 0.5,
                                            borderRadius: 1,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        }}
                                    >
                                        <Iconify
                                            icon="solar:magnifer-bold"
                                            sx={{ fontSize: 18, color: "primary.main" }}
                                        />
                                    </Box>
                                </InputAdornment>
                            ),
                            endAdornment: value && (
                                <InputAdornment position="end">
                                    <Button
                                        size="small"
                                        onClick={onClear}
                                        sx={{
                                            minWidth: "auto",
                                            p: 0.5,
                                            borderRadius: 1,
                                            backgroundColor: alpha(theme.palette.grey[500], 0.1),
                                            "&:hover": {
                                                backgroundColor: alpha(theme.palette.grey[500], 0.2),
                                            },
                                        }}
                                    >
                                        <Iconify icon="solar:close-circle-bold" sx={{ fontSize: 16 }} />
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.grey[500], 0.02),
                                // Always keep 2px border to prevent layout shift
                                border: `2px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                                "&:hover": {
                                    backgroundColor: alpha(theme.palette.grey[500], 0.04),
                                    border: `2px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                },
                                "&.Mui-focused": {
                                    backgroundColor: "transparent",
                                    border: `2px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                "&.MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                            },
                            "& .MuiInputBase-input": {
                                py: 1.5,
                                fontSize: "0.95rem",
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                    opacity: 0.7,
                                },
                            },
                        }}
                    />
                </Box>
            </Card>

            {/* Search Results Info */}
            {value && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {filteredCount === 0
                            ? "No prompts found matching your search"
                            : `Found ${filteredCount} prompt${filteredCount === 1 ? "" : "s"} matching "${value}"`}
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default CustomSearchBar;
